import moment from "moment";

import type { RioTrip, RioTripsFilters, TpgTrip } from "@types";
import { isTripIdentifier } from "./routingDeepLink";

export interface TripDriverOption {
  id: string | number;
  rioDriverId?: string;
}

export interface TripVehicleOption {
  id: string | number;
}

export interface TripSelection {
  driverId: string;
  vehicleId: string;
  driverMatched: boolean;
  vehicleMatched: boolean;
}

const ROUTE_ARROW = "->";

const splitFormattedRoute = (formattedRoute?: string | null): string[] | null => {
  const raw = formattedRoute?.trim();

  if (!raw) return null;

  const posicao = raw.indexOf(ROUTE_ARROW);

  if (posicao < 0) return null;

  const origem = raw.slice(0, posicao).trim();
  const destino = raw.slice(posicao + ROUTE_ARROW.length).trim();

  return origem && destino ? [origem, destino] : null;
};

export const resolveTripEndpoints = (trip: RioTrip): string[] => {
  const daRotaFormatada = splitFormattedRoute(trip.formattedRoute);

  if (daRotaFormatada) return daRotaFormatada;

  const extremos = [trip.origin?.trim(), trip.destination?.trim()].filter((parte): parte is string => !!parte);

  if (extremos.length > 0) return extremos;

  const alternativa = trip.formattedRoute?.trim() || trip.routeName?.trim() || trip.id;

  return alternativa ? [alternativa] : [];
};

export const formatTripDateTime = (startDateTime?: string | null): string | undefined => {
  const raw = startDateTime?.trim();

  if (!raw) return undefined;

  const data = moment(raw, moment.ISO_8601, true);

  if (!data.isValid()) return undefined;

  return data.format(raw.includes("T") ? "DD/MM/YYYY HH:mm" : "DD/MM/YYYY");
};

export const buildTripOptionLabel = (trip: RioTrip): string => {
  const partes = resolveTripEndpoints(trip);
  const dataHora = formatTripDateTime(trip.startDateTime);

  if (dataHora) partes.push(dataHora);
  if (trip.vehicleName?.trim()) partes.push(trip.vehicleName.trim());

  return partes.join(" - ");
};

export const toTripOptions = (trips: RioTrip[]): { id: string; label: string }[] =>
  trips.filter((trip) => !!trip?.id).map((trip) => ({ id: trip.id, label: buildTripOptionLabel(trip) }));

export const mergeSelectedTrip = (trips: RioTrip[], selected?: RioTrip | null): RioTrip[] => {
  if (!selected?.id) return trips;

  return trips.some((trip) => trip.id === selected.id) ? trips : [selected, ...trips];
};

export const TPG_TRIP_DESCRIPTION_MAX_LENGTH = 100;

export const normalizeTpgTripDescription = (value?: string | null): string => (value ?? "").trim().replace(/\s+/g, " ");

export const tpgTripToRioTrip = (trip: TpgTrip): RioTrip => ({
  id: trip.description,
  routeName: trip.description,
});

export const mergeTripSources = (routeTrips: RioTrip[], tpgTrips: RioTrip[]): RioTrip[] => {
  const ids = new Set(routeTrips.map((trip) => trip.id));

  return [...routeTrips, ...tpgTrips.filter((trip) => !ids.has(trip.id))];
};

export const resolveEditTrip = (trip?: RioTrip | null, tripCode?: string | null): RioTrip | null => {
  if (trip?.id) return trip;

  const code = tripCode?.trim();

  return code && !isTripIdentifier(code) ? { id: code, routeName: code } : null;
};

export const resolveTripLabel = (trip?: RioTrip | null, fallback?: string | null): string | null => {
  const label = trip?.id ? buildTripOptionLabel(trip).trim() : "";

  return label || fallback?.trim() || null;
};

const TRIP_DATE_FORMATS = ["YYYY-MM-DD", "DD/MM/YYYY"];

export const toTripFilterDate = (value?: string | null): string | undefined => {
  const raw = value?.trim();

  if (!raw) return undefined;

  const parsed = moment(raw, TRIP_DATE_FORMATS, true);

  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : undefined;
};

// A listagem de viagens nao filtra por data nem por motorista: o Route ignora
// startDate/endDate (espera initial_date/final_date) e o filtro em memoria do
// backend so alcanca a pagina corrente, escondendo viagens validas.
// toTripFilterDate segue disponivel.
export const buildTripFilters = (params: { vehicleId?: string | null; search?: string | null }): RioTripsFilters => {
  const filters: RioTripsFilters = {};
  const vehicleId = params.vehicleId?.trim();
  const search = params.search?.trim();

  if (vehicleId) filters.vehicleId = vehicleId;
  if (search) filters.search = search;

  return filters;
};

export const resolveTripSelection = (
  trip: RioTrip | undefined,
  drivers: TripDriverOption[],
  vehicles: TripVehicleOption[]
): TripSelection | null => {
  if (!trip) return null;

  const driverOption = drivers.find((driver) => !!driver.rioDriverId && driver.rioDriverId === trip.driverId);
  const vehicleOption = vehicles.find((vehicle) => !!trip.vehicleId && String(vehicle.id) === String(trip.vehicleId));

  return {
    driverId: driverOption ? String(driverOption.id) : "",
    vehicleId: vehicleOption ? String(vehicleOption.id) : "",
    driverMatched: !!driverOption,
    vehicleMatched: !!vehicleOption,
  };
};
